import React, { useEffect } from 'react';

import type { AiChatReference } from '~/components/AiChat/types';
import { useClaimReference } from '~/components/Clive/ClaimReferences/ClaimReferencesProvider';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { useCms } from '~/components/hooks/useCms';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import { DocumentReferenceTrigger } from './DocumentReferenceTrigger';
import { isLabelOnlyKind, REFERENCE_KINDS } from './referenceKinds';
import { WebLinkTrigger } from './WebLinkTrigger';

/**
 * InlineReferenceLink
 *
 * Public API component that routes to appropriate link handlers.
 *
 * Handles three types of links:
 * 1. Source references (src:<kind>:<id>) - a document, note or communication (`doc`), an expert report
 *    (`expert`) or another source the AI service resolves, each drawn as `REFERENCE_KINDS` says
 * 2. Web URLs (http/https or src:web:*) - external links with preview
 * 3. Invalid links - shown as broken with error tooltip
 *
 * A reference is taken from `references` where the caller resolved one (chat and insights resolve theirs
 * server-side), and otherwise from the claim's reference cache where one is mounted — the conductor's text
 * reaches the browser without a resolved map, so its citations ask here. While the cache is still
 * answering, a citation of any kind is its plain label: an id that has not been looked up yet is not a
 * missing one.
 *
 * A reference CMS cannot show (an unknown kind, an id that does not resolve) is dropped to its plain label,
 * so the sentence still reads, with a console warning naming the href and reason.
 *
 * With CONDUCTOR_EVIDENCE_REFS off, links render exactly as before the flag (`LegacyReferenceLink`).
 */

type DropReason = 'unknown kind' | 'unresolved';

interface InlineReferenceLinkProps {
  href?: string;
  children?: React.ReactNode;
  references?: Record<string, AiChatReference>;
}

const REF_PATTERN = /^src:([a-z]+)(?::(.+))?$/;
const DOC_URL_PATTERN = /^src:doc:(.+)$/;
const WEB_URL_PATTERN = /^src:web:(.+)$/;

const isValidWebUrl = (href: string): boolean => {
  try {
    const url = new URL(href);
    // Only allow http and https protocols to prevent XSS (javascript:) and unsupported protocols (ftp:, etc.)
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

const EvidenceReferenceLink: React.FC<InlineReferenceLinkProps> = ({ href = '', children, references }) => {
  // A web link is not a reference to resolve, so `src:web:` never reaches the kinds.
  const webMatch = href.match(WEB_URL_PATTERN);
  const [, kindName, refId] = (webMatch ? null : href.match(REF_PATTERN)) ?? [];
  // Own entries only, so a kind named after an Object.prototype member, such as `constructor`, is unknown too.
  const entry = kindName && Object.hasOwn(REFERENCE_KINDS, kindName) ? REFERENCE_KINDS[kindName] : undefined;
  const labelOnly = entry ? isLabelOnlyKind(entry) : false;
  // Only a label-only kind may come without an id; any other href lacking one stays a broken link.
  const isReference = Boolean(kindName) && (refId !== undefined || labelOnly);
  const kind = isReference && entry && !isLabelOnlyKind(entry) ? entry : undefined;
  const cacheKey = kind && refId ? kind.cacheKey(refId) : undefined;
  const providedReference = cacheKey ? references?.[cacheKey] : undefined;
  // Only ids the caller could not resolve are asked of the cache.
  const fromCache = useClaimReference(providedReference ? undefined : cacheKey);
  const reference = providedReference ?? fromCache.reference;

  let dropReason: DropReason | undefined;
  if (isReference && !entry) {
    dropReason = 'unknown kind';
  } else if (cacheKey && !reference && !fromCache.isPending) {
    dropReason = 'unresolved';
  }

  // Keyed on the outcome, so a dropped ref warns once, not on every render.
  useEffect(() => {
    if (dropReason) {
      // eslint-disable-next-line no-console
      console.warn(`InlineReferenceLink: dropped ${href} (${dropReason})`);
    }
  }, [dropReason, href]);

  // Dropped, still pending or label-only: the plain label, so the sentence around it still reads.
  if (dropReason || labelOnly || (kind && !reference)) {
    return <span>{children}</span>;
  }

  if (kind && reference) {
    return <kind.Trigger reference={reference}>{children}</kind.Trigger>;
  }

  // Check if it's a web URL with src:web: prefix (extract the actual URL)
  const actualUrl = webMatch ? webMatch[1] : href;

  // Check if it's a valid web URL
  if (isValidWebUrl(actualUrl)) {
    return <WebLinkTrigger href={actualUrl}>{children}</WebLinkTrigger>;
  }

  // Invalid link
  return (
    <Tooltip title="Broken link">
      <span className="text-red-600 cursor-help">{children}</span>
    </Tooltip>
  );
};

// ponytail: master's renderer, verbatim, so the flag-off path cannot drift; delete with the flag.
const LegacyReferenceLink: React.FC<InlineReferenceLinkProps> = ({ href = '', children, references }) => {
  const docMatch = href.match(DOC_URL_PATTERN);
  const documentSourceId = docMatch?.[1];
  const providedReference = documentSourceId ? references?.[documentSourceId] : undefined;
  const fromCache = useClaimReference(providedReference ? undefined : documentSourceId);

  if (documentSourceId) {
    const docReference = providedReference ?? fromCache.reference;

    if (!docReference) {
      return fromCache.isPending ? (
        <span>{children}</span>
      ) : (
        <Tooltip title="Reference not found">
          <span className="text-red-600 cursor-help">{children}</span>
        </Tooltip>
      );
    }

    return <DocumentReferenceTrigger reference={docReference}>{children}</DocumentReferenceTrigger>;
  }

  const webMatch = href.match(WEB_URL_PATTERN);
  const actualUrl = webMatch ? webMatch[1] : href;

  if (isValidWebUrl(actualUrl)) {
    return <WebLinkTrigger href={actualUrl}>{children}</WebLinkTrigger>;
  }

  return (
    <Tooltip title="Broken link">
      <span className="text-red-600 cursor-help">{children}</span>
    </Tooltip>
  );
};

export const InlineReferenceLink: React.FC<InlineReferenceLinkProps> = (props) => {
  const { userOrganization } = useCms();
  return isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONDUCTOR_EVIDENCE_REFS) ? (
    <EvidenceReferenceLink {...props} />
  ) : (
    <LegacyReferenceLink {...props} />
  );
};
